import React, { useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    TextareaAutosize
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AIIcon from '../../../assets/svgs/AI-Icon.svg';


const AiChatTab = ({
    aiSettings,
    chatMessage,
    setChatMessage,
    chatLoading,
    conversationHistory,
    chatContainerRef,
    setChatContainerRef,
    copiedMessageIndex,
    showReviewButton,
    handleChatWithAI,
    handleCopyMessage,
    handleApplySuggestion,
    handleTestAIMessage,
    handleReviewDescription,
    question
}) => {
    const handleTestPromptInChat = (promptContent) => {
        const testMessage = `Please test this specific prompt:\n\n"${promptContent}"\n\nFor the question: "${question?.order?.label || question?.label}"\n\nMy test data is: [Please provide your test data here]`;
        
        // Add the test message to chat
        setChatMessage(testMessage);
        
        // Auto-scroll to input
        setTimeout(() => {
            if (chatContainerRef) {
                chatContainerRef.scrollTop = chatContainerRef.scrollHeight;
            }
        }, 100);
    };

    // Auto-scroll to bottom when new messages are added
    useEffect(() => {
        if (chatContainerRef) {
            chatContainerRef.scrollTop = chatContainerRef.scrollHeight;
        }
    }, [conversationHistory, chatContainerRef]);
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: '#f7f8fa',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid #e1e5e9'
        }}>
            {/* Chat Header */}
            <Box sx={{
                padding: '16px 20px',
                backgroundColor: 'white',
                borderBottom: '1px solid #e1e5e9',
                borderRadius: '12px 12px 0 0'
            }}>
                <Typography variant="h6" sx={{
                    fontWeight: 600,
                    color: '#2c3e50',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <ChatIcon color="primary" />
                    AI Assistant
                </Typography>
                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                    Get help improving your AI prompt. Ask questions, request suggestions, or troubleshoot issues.
                </Typography>
            </Box>



            {/* Chat Messages Container */}
            <Box sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '400px'
            }}>
                <Box
                    ref={setChatContainerRef}
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px 20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        maxHeight: '500px',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#c1c1c1',
                            borderRadius: '3px',
                            '&:hover': {
                                backgroundColor: '#a8a8a8',
                            },
                        },
                    }}
                >
                    {conversationHistory.length === 0 ? (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#6c757d',
                            flexDirection: 'column',
                            gap: '16px'
                        }}>
                            <ChatIcon sx={{ fontSize: '48px', opacity: 0.3 }} />
                            <Typography sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                                Start a conversation with AI to improve your prompt...
                            </Typography>
                        </Box>
                    ) : (
                        conversationHistory.map((message, index) => (
                            <Box key={index} sx={{
                                display: 'flex',
                                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                                alignItems: 'flex-start',
                                gap: '8px',
                                marginBottom: '8px'
                            }}>
                                {/* AI Avatar */}
                                {message.role === 'assistant' && (
                                    <Box sx={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        backgroundColor: '#1976d2',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        marginTop: '4px'
                                    }}>
                                        <AIIcon sx={{ color: 'white', fontSize: '18px' }} />
                                    </Box>
                                )}

                                {/* Message Bubble */}
                                <Box sx={{
                                    maxWidth: '85%',
                                    minWidth: '150px'
                                }}>
                                    <Paper sx={{
                                        padding: '10px 14px',
                                        backgroundColor: message.role === 'user' ? '#1976d2' : 'white',
                                        color: message.role === 'user' ? 'white' : '#2c3e50',
                                        borderRadius: message.role === 'user'
                                            ? '16px 16px 4px 16px'
                                            : '16px 16px 16px 4px',
                                        boxShadow: message.role === 'user'
                                            ? '0 1px 4px rgba(25, 118, 210, 0.2)'
                                            : '0 1px 4px rgba(0,0,0,0.08)',
                                        border: message.role === 'assistant' ? '1px solid #e1e5e9' : 'none',
                                        maxWidth: '85%'
                                    }}>
                                        <Typography variant="body2" sx={{
                                            whiteSpace: 'pre-wrap',
                                            lineHeight: 1.5,
                                            fontSize: '14px'
                                        }}>
                                            {message.content}
                                        </Typography>
                                    </Paper>

                                    {/* Action Buttons for AI Messages */}
                                    {message.role === 'assistant' && (
                                        <Box sx={{
                                            display: 'flex',
                                            gap: '8px',
                                            marginTop: '8px',
                                            alignItems: 'center'
                                        }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={copiedMessageIndex === index ? <CheckIcon /> : <ContentCopyIcon />}
                                                onClick={() => handleCopyMessage(message.content, index)}
                                                sx={{
                                                    fontSize: '11px',
                                                    padding: '4px 8px',
                                                    minWidth: 'auto',
                                                    height: '24px',
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    borderColor: '#d1d5db',
                                                    color: '#6b7280',
                                                    '&:hover': {
                                                        borderColor: '#9ca3af',
                                                        backgroundColor: '#f9fafb'
                                                    }
                                                }}
                                            >
                                                {copiedMessageIndex === index ? 'Copied!' : 'Copy'}
                                            </Button>

                                            {/* Use as Description Button */}
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => handleApplySuggestion(message.content)}
                                                sx={{
                                                    fontSize: '11px',
                                                    padding: '4px 8px',
                                                    minWidth: 'auto',
                                                    height: '24px',
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    backgroundColor: '#1976d2',
                                                    '&:hover': {
                                                        backgroundColor: '#1565c0'
                                                    }
                                                }}
                                            >
                                                Use as Description
                                            </Button>

                                            {/* Test Prompt Button */}
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<PlayArrowIcon />}
                                                onClick={() => handleTestPromptInChat(message.content)}
                                                sx={{
                                                    fontSize: '11px',
                                                    padding: '4px 8px',
                                                    minWidth: 'auto',
                                                    height: '24px',
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    borderColor: '#f59e0b',
                                                    color: '#f59e0b',
                                                    '&:hover': {
                                                        backgroundColor: '#f59e0b',
                                                        color: 'white'
                                                    }
                                                }}
                                            >
                                                Test Prompt
                                            </Button>

                                            {/* AI Test Prompt Button */}
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<PlayArrowIcon />}
                                                onClick={() => {                                                    
                                                    handleTestAIMessage(message.content);
                                                }}
                                                sx={{
                                                    fontSize: '11px',
                                                    padding: '4px 8px',
                                                    minWidth: 'auto',
                                                    height: '24px',
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    borderColor: '#1976d2',
                                                    color: '#1976d2',
                                                    '&:hover': {
                                                        backgroundColor: '#1976d2',
                                                        color: 'white'
                                                    }
                                                }}
                                            >
                                                AI Test Prompt
                                            </Button>
                                        </Box>
                                    )}

                                    {/* Timestamp */}
                                    <Typography variant="caption" sx={{
                                        color: '#9ca3af',
                                        fontSize: '11px',
                                        marginTop: '4px',
                                        display: 'block',
                                        textAlign: message.role === 'user' ? 'right' : 'left'
                                    }}>
                                        {new Date(message.timestamp).toLocaleTimeString([], {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </Typography>
                                </Box>

                                {/* User Avatar */}
                                {message.role === 'user' && (
                                    <Box sx={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        backgroundColor: '#6b7280',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        marginTop: '4px'
                                    }}>
                                        <Typography sx={{ color: 'white', fontSize: '14px', fontWeight: 600 }}>
                                            U
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ))
                    )}

                    {/* Loading Indicator */}
                    {chatLoading && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            gap: '8px'
                        }}>
                            <Box sx={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                backgroundColor: '#1976d2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                marginTop: '4px'
                            }}>
                                <AIIcon sx={{ color: 'white', fontSize: '18px' }} />
                            </Box>
                            <Paper sx={{
                                padding: '12px 16px',
                                backgroundColor: 'white',
                                borderRadius: '18px 18px 18px 4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '1px solid #e1e5e9'
                            }}>
                                <CircularProgress size={16} sx={{ color: '#1976d2' }} />
                                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                    AI is thinking...
                                </Typography>
                            </Paper>
                        </Box>
                    )}
                </Box>

                {/* Chat Input */}
                <Box sx={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-end',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e1e5e9'
                }}>
                    <TextareaAutosize
                        minRows={1}
                        maxRows={4}
                        placeholder="Ask AI about improving your prompt or test it with sample data..."
                        value={chatMessage}
                        onChange={(e) => setChatMessage(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleChatWithAI();
                            }
                        }}
                        disabled={chatLoading}
                        style={{
                            width: '100%',
                            minHeight: '40px',
                            maxHeight: '120px',
                            padding: '12px',
                            fontSize: '14px',
                            lineHeight: '1.4',
                            fontFamily: 'inherit',
                            border: chatLoading ? '1px solid #e0e0e0' : '1px solid #d1d5db',
                            borderRadius: '8px',
                            resize: 'vertical',
                            outline: 'none',
                            backgroundColor: chatLoading ? '#f3f4f6' : 'white',
                            color: chatLoading ? '#9ca3af' : '#374151',
                            transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = '#1976d2';
                            e.target.style.borderWidth = '2px';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.borderWidth = '1px';
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleChatWithAI}
                        disabled={!chatMessage.trim() || chatLoading}
                        sx={{
                            borderRadius: '8px',
                            minWidth: '44px',
                            height: '44px',
                            backgroundColor: '#1976d2',
                            boxShadow: '0 2px 6px rgba(25, 118, 210, 0.2)',
                            '&:hover': {
                                backgroundColor: '#1565c0',
                                boxShadow: '0 3px 8px rgba(25, 118, 210, 0.3)'
                            },
                            '&:disabled': {
                                backgroundColor: '#e0e0e0',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        <SendIcon sx={{ fontSize: '18px' }} />
                    </Button>
                </Box>

                {/* Review Button - shown after applying suggestion */}
                {showReviewButton && (
                    <Box sx={{
                        padding: '8px 12px',
                        backgroundColor: '#e8f5e8',
                        border: '1px solid #4caf50',
                        borderRadius: '8px',
                        margin: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <CheckIcon sx={{ color: '#2e7d32', fontSize: '16px' }} />
                            <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                                Description applied! Review in Configuration tab.
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<VisibilityIcon sx={{ fontSize: '14px' }} />}
                            onClick={handleReviewDescription}
                            sx={{
                                backgroundColor: '#4caf50',
                                color: 'white',
                                borderRadius: '6px',
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '11px',
                                padding: '4px 8px',
                                minWidth: 'auto',
                                height: '24px',
                                '&:hover': {
                                    backgroundColor: '#45a049'
                                }
                            }}
                        >
                            Review
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default AiChatTab;